/** @jsxImportSource @compiled/react */
import React from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- React must be in scope for JSX in Jest
void React;

import { defineMessages, useIntl } from 'react-intl';
import Avatar from '@atlaskit/avatar';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import LocationIcon from '@atlaskit/icon/core/location';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { SpaceContributor } from './useSpaceContributors.ts';

const i18n = defineMessages({
	activity: {
		id: 'space-contributors.card.activity',
		defaultMessage:
			'{hasCreated, select, true {{createdCount, plural, one {Created # page} other {Created # pages}}{hasEdited, select, true {, } other {}}} other {}}{hasEdited, select, true {{editedCount, plural, one {edited # page} other {edited # pages}}} other {}}',
		description: 'Summary of pages created and edited by a contributor',
	},
});

export interface ContributorCardProps {
	contributor: SpaceContributor;
}

const styles = cssMap({
	card: {
		display: 'flex',
		flexDirection: 'column',
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		overflow: 'hidden',
	},
	header: {
		height: '44px',
		backgroundColor: token('color.background.accent.blue.subtlest'),
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		paddingInline: token('space.200'),
		paddingBlockEnd: token('space.200'),
		marginBlockStart: token('space.negative.250'),
		gap: token('space.100'),
	},
	details: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
	},
	locationPill: {
		display: 'flex',
		alignItems: 'center',
		height: '24px',
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.medium'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		paddingInline: token('space.075'),
		marginBlockStart: token('space.200'),
	},
});

export const ContributorCard = ({ contributor }: ContributorCardProps) => {
	const intl = useIntl();
	return (
		<Box xcss={styles.card} testId={`contributor-card-${contributor.accountId}`}>
			<Box xcss={styles.header} />
			<Box xcss={styles.content}>
				<Avatar
					name={contributor.displayName}
					src={contributor.avatarUrl}
					size="large"
					borderColor="transparent"
				/>
				<Heading size="small">{contributor.displayName}</Heading>
				<Box xcss={styles.details}>
					{contributor.jobTitle && (
						<Text color="color.text.subtlest" size="small" weight="bold">
							{contributor.jobTitle}
						</Text>
					)}
					{((contributor.created ?? 0) > 0 || (contributor.updated ?? 0) > 0) && (
						<Text color="color.text.subtlest" size="small">
							{intl.formatMessage(i18n.activity, {
								hasCreated: String((contributor.created ?? 0) > 0),
								createdCount: contributor.created ?? 0,
								hasEdited: String((contributor.updated ?? 0) > 0),
								editedCount: contributor.updated ?? 0,
							})}
						</Text>
					)}
				</Box>
				{contributor.location && (
					<Box xcss={styles.locationPill}>
						<Inline alignBlock="center" space="space.050">
							<LocationIcon label="" color={token('color.icon.subtlest')} />
							<Text color="color.text.subtlest" size="small">
								{contributor.location}
							</Text>
						</Inline>
					</Box>
				)}
			</Box>
		</Box>
	);
};
